import { EXTERNAL_SCRIPTS } from './providers';

let injecting = false;

/**
 * Ensures TikTok's `embed.js` processes any unprocessed `.tiktok-embed`
 * blockquotes on the page.
 *
 * `embed.js` scans the DOM and renders blockquotes when it runs, inserting the
 * player iframe inside each blockquote, but exposes no public reprocess API
 * (`window.tiktokEmbed` only surfaces `lib.receiveMessage`). So we (re)inject a
 * single script to (re)trigger a scan — which covers the first mount and App
 * Router client navigation, where a new blockquote appears without a page load.
 *
 * To avoid the redundant per-instance/per-mount tags a naive approach creates,
 * we: (1) inject only when at least one blockquote is still unprocessed (has no
 * iframe child), (2) collapse concurrent mounts behind an `injecting` guard, and
 * (3) remove any prior `embed.js` tag before adding a fresh one, so at most one
 * script tag exists at a time and multiple embeds on a page share one scan.
 */
export function ensureTikTokEmbedsProcessed(): void {
  if (typeof document === 'undefined' || injecting) return;

  const hasUnprocessed = Array.from(
    document.querySelectorAll<HTMLElement>('blockquote.tiktok-embed'),
  ).some((blockquote) => !blockquote.querySelector('iframe'));
  if (!hasUnprocessed) return;

  injecting = true;
  document
    .querySelectorAll(`script[src="${EXTERNAL_SCRIPTS.tiktok}"]`)
    .forEach((script) => script.remove());

  const script = document.createElement('script');
  script.src = EXTERNAL_SCRIPTS.tiktok;
  script.async = true;
  const release = () => {
    injecting = false;
  };
  script.addEventListener('load', release);
  script.addEventListener('error', release);
  document.body.appendChild(script);
}
